import "./Locks.css";

import { Button, Callout, Chip, Field, Icons, KeyValue, Panel, TextArea } from "../../../ds";
import { ILockService, lockErrorKind, lockErrorMessage, lockService } from "../services/LockService";
import { LockPrincipalDTO, PageLockDTO, PageLockStateDTO } from "../models/LockDTO";
import { useCallback, useEffect, useState } from "react";

import { ExternalLinkButton } from "./ExternalLinkButton";
import { IdentityUnavailableText } from "./LockStates";
import { Link } from "react-router-dom";
import { formatLockDate } from "../utils/lockDates";
import { identityWebIamUrl } from "../utils/identityWeb";

interface ILockPanelProps {
    spaceId: string;
    pageId: string;
    /** Injected in tests; the shared facade client otherwise. */
    service?: ILockService;
    /** The caller's object id, to say "you" for the creator. */
    currentUserId?: string;
    /** After a lock or unlock went through (refresh the tree badges). */
    onChanged?: () => void;
    /** The page is no longer visible to the caller (they locked it and are not one of its readers yet). */
    onHidden?: () => void;
}

type Status = "loading" | "absent" | "unavailable" | "error" | "ready";
type Pending = "lock" | "unlock" | undefined;

export const LockNote =
    "Locking hides a page and its children from everyone except the lock's readers, space Owners and platform admins.";
const LockWarning =
    "Until Ark manages readers itself, you are not added as a reader automatically: unless you are a space Owner or a platform admin, " +
    "the page disappears for you until an Identity admin adds you as Lock Reader or Lock Owner in Identity Web.";
const ReadersInIdentityWeb = "Readers are managed in Identity Web until Ark can manage them";
const CannotLock =
    "You cannot lock pages in this space: locking needs the Ark Contributor or Ark Owner role on the space.";
const CannotUnlock = "Only the lock's owners, space Owners and platform admins can unlock this page.";
const LoadFailed = "The lock details of this page could not be loaded.";

function isLocked(state: PageLockStateDTO | undefined): state is PageLockDTO {
    return !!state && state.locked === true;
}

function principalLabel(p: LockPrincipalDTO): string {
    return p.displayName || p.email || p.id;
}

const typeLabels: Record<string, string> = { group: "group", servicePrincipal: "service" };

function PrincipalIcon(props: { type?: string | null }) {
    if (props.type === "group") return <Icons.group size={16} />;
    if (props.type === "servicePrincipal") return <Icons.servicePrincipal size={16} />;
    return <Icons.user size={16} />;
}

function PrincipalList(props: { label: string; principals: LockPrincipalDTO[]; currentUserId?: string }) {
    const listLabel = `Lock ${props.label.toLowerCase()}`;
    return (
        <div className="ark-lock-people-col">
            <h4 className="ow-eyebrow">{props.label}</h4>
            <ul className="ark-lock-people-list" aria-label={listLabel}>
                {props.principals.length === 0 ? <li className="ark-muted">None</li> : null}
                {props.principals.map((p) => {
                    const type = p.type && p.type !== "user" ? typeLabels[p.type] ?? p.type : undefined;
                    return (
                        <li key={p.id} title={p.email || p.id}>
                            <span className="ark-lock-person-icon" aria-hidden="true"><PrincipalIcon type={p.type} /></span>
                            <span className={p.type === "servicePrincipal" ? "ark-mono" : undefined}>{principalLabel(p)}</span>
                            {props.currentUserId && p.id === props.currentUserId ? <span className="ark-lock-you">you</span> : null}
                            {type ? <Chip size="sm" tone="neutral">{type}</Chip> : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/**
 * The lock panel of a page (OrbitalPlan/18 D8), the inline card between the page header and the body (board `Ark-Page`). Only the
 * page's readers ever see it: for anyone else the page is a 404. The SPA cannot evaluate `ark.locks/create` or `ark.locks/manage`
 * itself, so Lock and Unlock are offered and a 403 is explained instead.
 */
export function LockPanel(props: ILockPanelProps) {
    const service = props.service ?? lockService();
    const { spaceId, pageId, onChanged, onHidden } = props;
    const [status, setStatus] = useState<Status>("loading");
    const [state, setState] = useState<PageLockStateDTO>();
    const [pending, setPending] = useState<Pending>();
    const [reason, setReason] = useState("");
    const [isBusy, setIsBusy] = useState(false);
    const [actionError, setActionError] = useState<string>();

    const applyLoadError = useCallback((err: unknown) => {
        const kind = lockErrorKind(err);
        // 404: a server without lock routes (pre-1b) or a page gone meanwhile; either way there is nothing to show
        setStatus(kind === "not-found" ? "absent" : kind === "identity-unavailable" ? "unavailable" : "error");
    }, []);

    // another page: back to loading, with no half-finished lock / unlock (adjusted while rendering, not in the effect)
    const pageKey = `${spaceId}/${pageId}`;
    const [shownKey, setShownKey] = useState(pageKey);
    if (shownKey !== pageKey) {
        setShownKey(pageKey);
        setStatus("loading");
        setPending(undefined);
        setActionError(undefined);
    }

    useEffect(() => {
        let alive = true;
        service
            .getLock(spaceId, pageId)
            .then((s) => {
                if (!alive) return;
                setState(s);
                setStatus("ready");
            })
            .catch((err) => alive && applyLoadError(err));
        return () => {
            alive = false;
        };
    }, [service, spaceId, pageId, applyLoadError]);

    const actionFailed = useCallback((err: unknown, action: Exclude<Pending, undefined>) => {
        const kind = lockErrorKind(err);
        if (kind === "forbidden") setActionError(action === "lock" ? CannotLock : CannotUnlock);
        else if (kind === "identity-unavailable") setActionError(IdentityUnavailableText);
        else setActionError(lockErrorMessage(err));
        setIsBusy(false);
    }, []);

    /** Re-reads the lock after a change; a 404 now means the caller no longer reads the page. */
    const refreshAfterChange = useCallback(() => {
        return service.getLock(spaceId, pageId).then(
            (s) => {
                setState(s);
                setStatus("ready");
                setPending(undefined);
                setReason("");
                setIsBusy(false);
                onChanged?.();
            },
            (err) => {
                setIsBusy(false);
                setPending(undefined);
                if (lockErrorKind(err) === "not-found") {
                    onChanged?.();
                    onHidden?.();
                } else applyLoadError(err);
            }
        );
    }, [service, spaceId, pageId, onChanged, onHidden, applyLoadError]);

    const confirmLock = useCallback(() => {
        setIsBusy(true);
        setActionError(undefined);
        service.lock(spaceId, pageId, reason).then(refreshAfterChange, (err) => actionFailed(err, "lock"));
    }, [service, spaceId, pageId, reason, refreshAfterChange, actionFailed]);

    const confirmUnlock = useCallback(() => {
        setIsBusy(true);
        setActionError(undefined);
        service.unlock(spaceId, pageId).then(refreshAfterChange, (err) =>
            // 404: the page carries no lock of its own any more (someone else unlocked it): show the current state
            lockErrorKind(err) === "not-found" ? refreshAfterChange() : actionFailed(err, "unlock")
        );
    }, [service, spaceId, pageId, refreshAfterChange, actionFailed]);

    const cancel = useCallback(() => {
        setPending(undefined);
        setActionError(undefined);
    }, []);

    if (status === "loading" || status === "absent") return null;

    if (status === "unavailable" || status === "error") {
        return (
            <Panel title="" aria-label="Page lock" className="ark-lock-panel">
                <Callout tone={status === "unavailable" ? "warning" : "danger"}>
                    {status === "unavailable" ? IdentityUnavailableText : LoadFailed}
                </Callout>
            </Panel>
        );
    }

    const lock = isLocked(state) ? state : undefined;

    if (!lock) {
        return (
            <Panel title="" aria-label="Page lock" className="ark-lock-panel">
                {pending === "lock" ? (
                    <div className="ark-lock-dialog" role="dialog" aria-label="Lock this page">
                        <h3 className="ark-lock-heading">Lock this page</h3>
                        <p className="ark-muted">{LockNote}</p>
                        <Callout tone="warning">{LockWarning}</Callout>
                        <Field label="Reason (optional)">
                            <TextArea value={reason} onChange={setReason} rows={3} maxLength={1000} disabled={isBusy}
                                placeholder="Why the page is locked, for its readers" />
                        </Field>
                        {actionError ? <Callout tone="danger">{actionError}</Callout> : null}
                        <div className="ark-row">
                            <Button text="Lock" type="primary" size="sm" icon={<Icons.lock />} onClick={confirmLock} isBusy={isBusy} />
                            <Button text="Cancel" type="secondary" size="sm" onClick={cancel} isDisabled={isBusy} />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="ark-lock-unlocked">
                            <span>This page is not locked.</span>
                            <Button text="Lock this page" type="secondary" size="sm" icon={<Icons.lock />}
                                onClick={() => setPending("lock")} isDisabled={isBusy} />
                        </div>
                        <p className="ark-lock-note">{LockNote}</p>
                    </>
                )}
            </Panel>
        );
    }

    const inherited = !!lock.inherited;
    const manageUrl = lock.manageUrl || identityWebIamUrl(lock.scope);
    const people = [...lock.owners, ...lock.readers];
    const creator = props.currentUserId && lock.createdBy === props.currentUserId
        ? "you"
        : (() => {
              const known = people.find((p) => p.id === lock.createdBy);
              return known ? principalLabel(known) : lock.createdBy;
          })();

    const facts = [
        { label: "Locked by", value: creator, id: "locked-by" },
        { label: "Locked on", value: formatLockDate(lock.createdAt), id: "locked-on" },
    ];

    return (
        <Panel title="" aria-label="Page lock" className={`ark-lock-panel is-locked${inherited ? " is-inherited" : ""}`}>
            <div className="ark-lock-head">
                <Chip size="sm" tone="accent" icon={<Icons.lock size={12} />}>Locked</Chip>
                <h3 className="ark-lock-heading">Hidden from everyone but its readers</h3>
            </div>
            {inherited ? (
                <p className="ark-lock-inherited">
                    This page is under the lock on{" "}
                    <Link to={`/spaces/${spaceId}/pages/${lock.pageId}`}>{lock.title}</Link>; it can be unlocked there.
                </p>
            ) : null}
            {manageUrl || (!inherited && pending !== "unlock") ? (
                <div className="ark-row">
                    {manageUrl ? <ExternalLinkButton href={manageUrl} text="Manage readers" /> : null}
                    {!inherited && pending !== "unlock" ? (
                        <Button text="Unlock" type="secondary" size="sm" icon={<Icons.unlock />} onClick={() => setPending("unlock")} isDisabled={isBusy} />
                    ) : null}
                </div>
            ) : null}
            {!inherited && pending === "unlock" ? (
                <div className="ark-lock-confirm" role="group" aria-label="Confirm unlock">
                    <p>Unlock this page? Everyone who reads the space will see it and its children again.</p>
                    {actionError ? <Callout tone="danger">{actionError}</Callout> : null}
                    <div className="ark-row">
                        <Button text="Confirm unlock" type="primary" size="sm" icon={<Icons.unlock />} onClick={confirmUnlock} isBusy={isBusy} />
                        <Button text="Cancel" type="secondary" size="sm" onClick={cancel} isDisabled={isBusy} />
                    </div>
                </div>
            ) : null}
            <p className="ark-lock-note">{LockNote}</p>
            <div className="ark-lock-facts">
                <KeyValue items={facts} columns={2} />
                {lock.reason ? <KeyValue items={[{ label: "Reason", value: lock.reason, id: "reason" }]} columns={1} /> : null}
            </div>
            {lock.owners.length === 0 && lock.readers.length === 0 ? (
                <p className="ark-lock-note">{ReadersInIdentityWeb}</p>
            ) : (
                <div className="ark-lock-people">
                    <PrincipalList label="Owners" principals={lock.owners} currentUserId={props.currentUserId} />
                    <PrincipalList label="Readers" principals={lock.readers} currentUserId={props.currentUserId} />
                </div>
            )}
        </Panel>
    );
}
